import {
  FETCH_COMPANY_PROFILE_REQUEST,
  FETCH_COMPANY_PROFILE_SUCCESS,
  FETCH_COMPANY_PROFILE_FAILURE,
  CHECK_COMPANY_PROFILE,
  PATCH_COMPANY_REQUEST,
  PATCH_COMPANY_SUCCESS,
  PATCH_COMPANY_FAILURE,
  FETCH_COMPANIES_REQUEST,
  FETCH_COMPANIES_SUCCESS,
  FETCH_COMPANIES_FAILURE,
} from './companyTypes';
import { getAuthClient } from '../../library/drupal/DrupalAuth2';

const auth = getAuthClient();

// JSON:API constants
const CONFIRM_RESOURCE = 'reporting_packet_confirmation';
const CONFIRM_TYPE = 'node--reporting_packet_confirmation';
const COMPANY_TYPE = 'node--appb_company_contact';
const SEASON_TYPE = 'node--season';

// Field names
const FIELD_BOOL_ON_CONFIRM = 'field_reporting_packet_confirm';
const FIELD_RELATION_COMPANY_ON_CONFIRM = 'field_appb_company';
const FIELD_RELATION_SEASON_ON_CONFIRM = 'field_season';
const FIELD_CONFIRMS_ON_COMPANY = 'field_reporting_packet_confirms';

const FIELD_CERTIFICATION_DATETIME = 'field_certification_datetime';
const FIELD_CERTIFICATION_NAME = 'field_certification_name';

// -------- Fetch single company profile --------
export const fetchCompanyProfile = (companyUID) => {
  if (companyUID !== null) {
    const url =
      '/jsonapi/node/appb_company_contact/' +
      companyUID +
      '?include=field_reporting_packet_confirms';
    const options = {
      method: 'GET',
      headers: new Headers({
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
      }),
      mode: 'cors',
    };
    return async (dispatch) => {
      dispatch(fetchCompanyProfileRequest()); // <-- FIXED
      try {
        const response = await auth.fetchWithAuthentication(url, options);
        if (response.ok) {
          const data = await response.json();
          const companyProfile = data.data;

          const companyName = companyProfile['title'];
          const companyRootAPPBID = companyProfile['field_appb_root_id'];
          const companyStatus = companyProfile['field_company_status'];

          const companyIsFirstHandler =
            Array.isArray(companyProfile['field_company_type']) &&
            companyProfile['field_company_type'].includes('first_handler');
          const companyIsImporter =
            Array.isArray(companyProfile['field_company_type']) &&
            companyProfile['field_company_type'].includes('importer');
          const companyIsProducer =
            Array.isArray(companyProfile['field_company_type']) &&
            companyProfile['field_company_type'].includes('producer');

          localStorage.setItem('company_name', companyName);
          localStorage.setItem('company_root_appb_id', companyRootAPPBID);
          localStorage.setItem('company_status', companyStatus);
          localStorage.setItem(
            'company_is_first_handler',
            String(companyIsFirstHandler)
          );
          localStorage.setItem(
            'company_is_importer',
            String(companyIsImporter)
          );
          localStorage.setItem(
            'company_is_producer',
            String(companyIsProducer)
          );
          localStorage.setItem(
            'company_profile',
            JSON.stringify(companyProfile)
          );

          await dispatch(
            fetchCompanyProfileSuccess(
              companyName,
              companyRootAPPBID,
              companyStatus,
              companyIsFirstHandler,
              companyIsImporter,
              companyIsProducer,
              companyProfile
            )
          );
        } else {
          throw new Error('message: ' + response.status);
        }
      } catch (error) {
        const errorMsg = error.message;
        dispatch(fetchCompanyProfileFailure(errorMsg));
      }
    };
  }
};

// ---- Relationship append helper ----
async function attachConfirmToCompany(companyId, confirmId, auth) {
  var relUrl =
    '/jsonapi/node/appb_company_contact/' +
    companyId +
    '/relationships/' +
    FIELD_CONFIRMS_ON_COMPANY;
  var postBody = { data: [{ type: CONFIRM_TYPE, id: confirmId }] };
  var postOptions = {
    method: 'POST',
    headers: new Headers({
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
    }),
    mode: 'cors',
    body: JSON.stringify(postBody),
  };
  var postResp = await auth.fetchWithAuthentication(relUrl, postOptions);
  if (!postResp.ok) {
    throw new Error(
      'Failed to append confirmation to company: ' + postResp.status
    );
  }
}

function isoNowSecondsUTC() {
  return new Date().toISOString().replace(/\.\d{3}Z$/, '+00:00');
}

// ---- Upsert reporting packet confirmation ----
export const upsertReportingPacketConfirm = (currentSeasonID) => {
  return async (dispatch, getState) => {
    dispatch(patchCompanyRequest());

    try {
      var state = getState();
      var companyReducer =
        state && state.companyReducer ? state.companyReducer : null;
      var seasonReducer =
        state && state.seasonReducer ? state.seasonReducer : null;
      var userReducer = state && state.userReducer ? state.userReducer : null;

      if (!companyReducer || !companyReducer.companyProfile)
        throw new Error('Company profile is not loaded.');
      if (!seasonReducer || !seasonReducer.currentSeason)
        throw new Error('Current season is not available.');
      if (!currentSeasonID) throw new Error('Missing current season ID.');

      var companyProfile = companyReducer.companyProfile;
      var companyId =
        companyProfile && companyProfile.id ? companyProfile.id : null;
      if (!companyId) throw new Error('Missing company ID.');

      var companyNumber = companyProfile.field_appb_root_id || '';
      var seasonName =
        seasonReducer.currentSeason && seasonReducer.currentSeason.title
          ? seasonReducer.currentSeason.title
          : '';
      var username =
        userReducer && userReducer.username ? userReducer.username : '';
      var nowIso = isoNowSecondsUTC();

      var searchUrl =
        '/jsonapi/node/' +
        CONFIRM_RESOURCE +
        '?filter[company][condition][path]=' +
        FIELD_RELATION_COMPANY_ON_CONFIRM +
        '.id' +
        '&filter[company][condition][value]=' +
        encodeURIComponent(companyId) +
        '&filter[season][condition][path]=' +
        FIELD_RELATION_SEASON_ON_CONFIRM +
        '.id' +
        '&filter[season][condition][value]=' +
        encodeURIComponent(currentSeasonID) +
        '&page[limit]=1';

      var getOptions = {
        method: 'GET',
        headers: new Headers({
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
        }),
        mode: 'cors',
      };

      var searchResp = await auth.fetchWithAuthentication(
        searchUrl,
        getOptions
      );
      if (!searchResp.ok)
        throw new Error(
          'Failed to search existing confirmations: ' + searchResp.status
        );
      var searchJson = await searchResp.json();
      var existing =
        searchJson && searchJson.data && searchJson.data.length > 0
          ? searchJson.data[0]
          : null;

      var computedTitle =
        'Reporting Packet Confirmation-' + companyNumber + '-' + seasonName;

      if (existing && existing.id) {
        var patchUrl = '/jsonapi/node/' + CONFIRM_RESOURCE + '/' + existing.id;
        var patchBody = {
          data: {
            type: CONFIRM_TYPE,
            id: existing.id,
            attributes: {},
          },
        };
        patchBody.data.attributes[FIELD_BOOL_ON_CONFIRM] = true;
        patchBody.data.attributes['title'] = computedTitle;
        patchBody.data.attributes[FIELD_CERTIFICATION_DATETIME] = nowIso;
        patchBody.data.attributes[FIELD_CERTIFICATION_NAME] = username;

        var patchOptions = {
          method: 'PATCH',
          headers: new Headers({
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
          }),
          mode: 'cors',
          body: JSON.stringify(patchBody),
        };

        var patchResp = await auth.fetchWithAuthentication(
          patchUrl,
          patchOptions
        );
        if (!patchResp.ok)
          throw new Error(
            'Failed to update existing confirmation: ' + patchResp.status
          );
      } else {
        var postUrl = '/jsonapi/node/' + CONFIRM_RESOURCE;
        var postBody = {
          data: {
            type: CONFIRM_TYPE,
            attributes: { title: computedTitle },
            relationships: {},
          },
        };
        postBody.data.attributes[FIELD_BOOL_ON_CONFIRM] = true;
        postBody.data.attributes[FIELD_CERTIFICATION_DATETIME] = nowIso;
        postBody.data.attributes[FIELD_CERTIFICATION_NAME] = username;

        postBody.data.relationships[FIELD_RELATION_SEASON_ON_CONFIRM] = {
          data: { type: SEASON_TYPE, id: currentSeasonID },
        };
        postBody.data.relationships[FIELD_RELATION_COMPANY_ON_CONFIRM] = {
          data: { type: COMPANY_TYPE, id: companyId },
        };

        var postOptions = {
          method: 'POST',
          headers: new Headers({
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
          }),
          mode: 'cors',
          body: JSON.stringify(postBody),
        };

        var postResp = await auth.fetchWithAuthentication(postUrl, postOptions);
        if (!postResp.ok)
          throw new Error(
            'Failed to create new confirmation: ' + postResp.status
          );
        var created = await postResp.json();
        var createdId =
          created && created.data && created.data.id ? created.data.id : null;

        if (createdId) {
          await attachConfirmToCompany(companyId, createdId, auth);
        }
      }

      dispatch(patchCompanySuccess());
      var companyUIDLocal = null;
      try {
        companyUIDLocal = localStorage.getItem('company_uid');
      } catch (e) {}
      await dispatch(fetchCompanyProfile(companyUIDLocal));
    } catch (error) {
      var msg = error && error.message ? error.message : 'Unknown error';
      dispatch(patchCompanyFailure(msg));
    }
  };
};

// ---- Simple action creators ----
export const fetchCompanyProfileRequest = () => ({
  type: FETCH_COMPANY_PROFILE_REQUEST,
});
export const fetchCompanyProfileSuccess = (
  companyName,
  companyRootAPPBID,
  companyStatus,
  companyIsFirstHandler,
  companyIsImporter,
  companyIsProducer,
  companyProfile
) => ({
  type: FETCH_COMPANY_PROFILE_SUCCESS,
  companyName: companyName,
  companyRootAPPBID: companyRootAPPBID,
  companyStatus: companyStatus,
  companyIsFirstHandler: companyIsFirstHandler,
  companyIsImporter: companyIsImporter,
  companyIsProducer: companyIsProducer,
  companyProfile: companyProfile,
});
export const fetchCompanyProfileFailure = (error) => ({
  type: FETCH_COMPANY_PROFILE_FAILURE,
  payload: error,
});

export const checkCompanyProfile = () => ({
  type: CHECK_COMPANY_PROFILE,
  companyName: localStorage.getItem('company_name'),
  companyRootAPPBID: localStorage.getItem('company_root_appb_id'),
  companyStatus: localStorage.getItem('company_status'),
  companyIsFirstHandler:
    localStorage.getItem('company_is_first_handler') === 'true',
  companyIsImporter: localStorage.getItem('company_is_importer') === 'true',
  companyIsProducer: localStorage.getItem('company_is_producer') === 'true',
  companyProfile: JSON.parse(localStorage.getItem('company_profile')),
  companies: JSON.parse(localStorage.getItem('companies')),
});

export const patchCompanyRequest = () => ({ type: PATCH_COMPANY_REQUEST });
export const patchCompanySuccess = () => ({ type: PATCH_COMPANY_SUCCESS });
export const patchCompanyFailure = (error) => ({
  type: PATCH_COMPANY_FAILURE,
  payload: error,
});

export const fetchCompanies = () => {
  const path = '/jsonapi/node/appb_company_contact';
  const filter =
    'filter[or-group][group][conjunction]=OR&filter[fh-filter][condition][path]=field_company_type&filter[fh-filter][condition][value]=first_handler&filter[fh-filter][condition][memberOf]=or-group&filter[importer-filter][condition][path]=field_company_type&filter[importer-filter][condition][value]=importer&filter[importer-filter][condition][memberOf]=or-group&page[limit]=1000';
  const url = path + '?' + filter;

  const options = {
    method: 'GET',
    headers: new Headers({
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
    }),
    mode: 'cors',
  };

  return async (dispatch) => {
    dispatch(fetchCompaniesRequest()); // <-- FIXED
    try {
      const response = await auth.fetchWithAuthentication(url, options);
      if (response.ok) {
        const data = await response.json();
        const companies = data.data;
        localStorage.setItem('companies', JSON.stringify(companies));
        await dispatch(fetchCompaniesSuccess(companies));
      } else {
        throw new Error('message: ' + response.status);
      }
    } catch (error) {
      const errorMsg = error.message;
      dispatch(fetchCompaniesFailure(errorMsg));
    }
  };
};

export const fetchCompaniesRequest = () => ({ type: FETCH_COMPANIES_REQUEST });
export const fetchCompaniesSuccess = (companies) => ({
  type: FETCH_COMPANIES_SUCCESS,
  companies: companies,
});
export const fetchCompaniesFailure = (error) => ({
  type: FETCH_COMPANIES_FAILURE,
  payload: error,
});
